import { ErrorView } from "@/components/ui/layout/error/errorView"
import { BoxSlotTemplateFormCreate } from "@/modules/boxSlotTemplate/components/BoxSlotTemplateFormCreate"
import { BoxSlotTemplateFormUpdate } from "@/modules/boxSlotTemplate/components/BoxSlotTemplateFormUpdate"
import { useBoxSlotTemplateActions } from "@/modules/boxSlotTemplate/store/useBoxSlotTemplateStore"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { BoxSlotTemplateGroupForm } from "@modules/boxSlotTemplate/components/boxSlotTemplateGroupForm"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

export const useBoxSlotTemplateSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  const confirmPopup = useConfirmPopup()
  const canAddSlotTemplate = useBoxStaffPermission(BoxStaffPermissions.MANAGE_TYPICAL_WEEK)
  const canEditSlotTemplate = useBoxStaffPermission(BoxStaffPermissions.MANAGE_TYPICAL_WEEK)
  const { clearBoxSlotTemplateBeingCreated } = useBoxSlotTemplateActions()

  const navigateToBoxSlotTemplateCreation = (boxProgramId: string, date: Date) => {
    // PERMISSIONS - Head-coach && Coach & Financial permissions denied
    if (!canAddSlotTemplate) {
      return null
    }

    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotTemplateFormCreate boxProgramId={boxProgramId} dateToCreateSlotTemplateOn={date} />
          </Suspense>
        </ErrorBoundary>
      ),
      onHide: () => {
        void clearBoxSlotTemplateBeingCreated()
        return Promise.resolve(true)
      },
    })
  }

  const navigateToBoxSlotTemplateEdition = (boxProgramId: string, boxSlotTemplateId: string) => {
    // PERMISSIONS - Coach & Financial permissions denied
    if (!canEditSlotTemplate) {
      return null
    }
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotTemplateFormUpdate boxSlotTemplateId={boxSlotTemplateId} boxProgramId={boxProgramId} />
          </Suspense>
        </ErrorBoundary>
      ),
      onHide: () => {
        void clearBoxSlotTemplateBeingCreated()
        return Promise.resolve(true)
      },
    })
  }

  const navigateToBoxSlotTemplateGroupCreation = (boxProgramId: string) => {
    // PERMISSIONS - Head-coach && Coach & Financial permissions denied
    if (!canAddSlotTemplate) {
      return null
    }

    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotTemplateGroupForm boxProgramId={boxProgramId} />
          </Suspense>
        </ErrorBoundary>
      ),
      allowSidebarReduction: true,
      replaceAllComponents: true,
      onHide: async () =>
        await new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("box.slotTemplateGroup.form.cancel.title"),
            message: word("box.slotTemplateGroup.form.cancel.message"),
            accept: () => {
              return Promise.resolve(resolve(true))
            },
            cancel: () => {
              return Promise.resolve(resolve(false))
            },
          })
        }),
    })
  }

  return {
    navigateToBoxSlotTemplateCreation,
    navigateToBoxSlotTemplateEdition,
    navigateToBoxSlotTemplateGroupCreation,
  }
}
